import { ref, computed, watch } from 'vue'
import {
  resolveRangeClick,
  isBetweenInclusiveHover,
  parseDateInput,
  formatDateInput,
  isSameDay
} from '../lib/dateRangeSelection.js'
import { buildCheckInDateTime, buildCheckOutDateTime } from '../lib/booking.js'
import {
  isNonBlockingBookingStatus,
  bookingRangesOverlap,
  BLOCKING_AVAILABILITY_KINDS
} from '../config/bookingAvailability.js'
import { trackCalendarSearch } from '../lib/analytixTracking.js'

export { parseDateInput, formatDateInput, isSameDay }

export const parseAvailabilityDate = (value) => {
  if (!value) return null

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) return null

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

export const formatDateDisplay = (value) => {
  const date = parseDateInput(value)
  if (!date) return ''

  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date)
}

const startOfToday = () => {
  const today = new Date()
  return new Date(today.getFullYear(), today.getMonth(), today.getDate())
}

const getVisibleCalendarRange = (monthDate) => {
  const year = monthDate.getFullYear()
  const month = monthDate.getMonth()
  const firstDay = new Date(year, month, 1)
  const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
  const calendarStart = new Date(year, month, 1 - startOffset)
  const calendarEnd = new Date(calendarStart)
  calendarEnd.setDate(calendarStart.getDate() + 41)

  return { calendarStart, calendarEnd }
}

export function useAvailabilityCalendar({
  apiStore,
  apartment,
  selectable = false,
  getCheckIn = () => '',
  getCheckOut = () => '',
  onRangeChange = null,
  active = true
}) {
  const currentMonth = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const requestId = ref(0)
  const hoverDate = ref(null)
  const selectionMessage = ref('')

  const isNonBlockingItem = (item) => isNonBlockingBookingStatus(item?.status)

  const availabilityItems = computed(() => apiStore.availability || [])
  const loading = computed(() => apiStore.availabilityLoading)
  const error = computed(() => apiStore.availabilityError)

  const monthLabel = computed(() =>
    new Intl.DateTimeFormat('de-DE', {
      month: 'long',
      year: 'numeric'
    }).format(currentMonth.value)
  )

  const occupiesNight = (date, kinds = BLOCKING_AVAILABILITY_KINDS) => {
    if (!availabilityItems.value.length) return false

    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    return availabilityItems.value.some((item) => {
      if (isNonBlockingItem(item)) return false
      if (!kinds.includes(item.availabilityKind || 'confirmed')) return false

      const start = parseAvailabilityDate(item.start)
      const end = parseAvailabilityDate(item.end)

      if (!start || !end) return false

      if (isSameDay(start, end)) {
        return isSameDay(normalizedDate, start)
      }

      return normalizedDate >= start && normalizedDate < end
    })
  }

  const isDateBooked = (date) => occupiesNight(date, ['confirmed', 'pending'])

  const isArrivalDay = (date, kinds = BLOCKING_AVAILABILITY_KINDS) => {
    if (!availabilityItems.value.length) return false

    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    return availabilityItems.value.some((item) => {
      if (isNonBlockingItem(item)) return false
      if (!kinds.includes(item.availabilityKind || 'confirmed')) return false
      const start = parseAvailabilityDate(item.start)
      return !!start && normalizedDate.getTime() === start.getTime()
    })
  }

  const isDepartureDay = (date, kinds = BLOCKING_AVAILABILITY_KINDS) => {
    if (!availabilityItems.value.length) return false

    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    return availabilityItems.value.some((item) => {
      if (isNonBlockingItem(item)) return false
      if (!kinds.includes(item.availabilityKind || 'confirmed')) return false
      const end = parseAvailabilityDate(item.end)
      return !!end && normalizedDate.getTime() === end.getTime()
    })
  }

  const isRangeAvailable = (startDate, endDate) => {
    if (!startDate || !endDate || endDate <= startDate) return false

    const reqStart = buildCheckInDateTime(formatDateInput(startDate))
    const reqEnd = buildCheckOutDateTime(formatDateInput(endDate))
    if (!reqStart || !reqEnd) return false

    return !availabilityItems.value.some((item) => {
      if (isNonBlockingItem(item)) return false
      if (!BLOCKING_AVAILABILITY_KINDS.includes(item.availabilityKind || 'confirmed')) return false

      const bookingStart = item.start || item.check_in
      const bookingEnd = item.end || item.check_out
      if (!bookingStart || !bookingEnd) return false

      return bookingRangesOverlap(bookingStart, bookingEnd, reqStart, reqEnd)
    })
  }

  const isPastDate = (date) => {
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    return normalizedDate < startOfToday()
  }

  const blocksCheckIn = (date) => isDateBooked(date) || isArrivalDay(date)

  const canClickDay = (date) => {
    if (isPastDate(date)) return false
    if (!selectable) return false

    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const selectedStart = parseDateInput(getCheckIn())
    const selectedEnd = parseDateInput(getCheckOut())

    // Anreise wählen (oder Zeitraum neu starten)
    if (!selectedStart || selectedEnd) {
      return !blocksCheckIn(normalizedDate)
    }

    // Klick auf/before Anreise → neue Anreise
    if (normalizedDate <= selectedStart) {
      return !blocksCheckIn(normalizedDate)
    }

    // Abreise wählen: Turnover erlaubt (Abreise 10:00, fremde Anreise 16:00)
    return isRangeAvailable(selectedStart, normalizedDate)
  }

  const isInSelectedRange = (date, startValue, endValue) => {
    const start = parseDateInput(startValue)
    const end = parseDateInput(endValue)
    if (!start || !end) return false

    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    return normalizedDate > start && normalizedDate < end
  }

  const selectionPhase = computed(() => {
    const checkIn = getCheckIn()
    const checkOut = getCheckOut()
    if (!checkIn) return 'pick-check-in'
    if (!checkOut) return 'pick-check-out'
    return 'complete'
  })

  const calendarDays = computed(() => {
    const month = currentMonth.value.getMonth()
    const { calendarStart } = getVisibleCalendarRange(currentMonth.value)
    const selectedStart = parseDateInput(getCheckIn())
    const selectedEnd = parseDateInput(getCheckOut())
    const hover = hoverDate.value
    const pickingCheckOut = selectable && selectedStart && !selectedEnd && hover
    const hoverRangeValid = pickingCheckOut ? isRangeAvailable(selectedStart, hover) : false

    const days = []

    for (let index = 0; index < 42; index += 1) {
      const day = new Date(calendarStart)
      day.setDate(calendarStart.getDate() + index)
      const booked = isDateBooked(day)
      const isPast = isPastDate(day)
      const isClickable = selectable && canClickDay(day)
      const isRangeStart = selectedStart && isSameDay(day, selectedStart)
      const isRangeEnd = selectedEnd && isSameDay(day, selectedEnd)
      const isInRange = isInSelectedRange(day, getCheckIn(), getCheckOut())
      const isHoverEnd = pickingCheckOut && hover && isSameDay(day, hover)
      const isInHoverRange =
        pickingCheckOut &&
        hoverRangeValid &&
        isBetweenInclusiveHover(day, selectedStart, hover)
      const isInHoverRangeInvalid =
        pickingCheckOut &&
        !hoverRangeValid &&
        isBetweenInclusiveHover(day, selectedStart, hover)

      days.push({
        key: `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`,
        label: day.getDate(),
        date: day,
        isBooked: booked,
        isArrival: isArrivalDay(day),
        isDeparture: isDepartureDay(day),
        isCurrentMonth: day.getMonth() === month,
        isPast,
        isClickable,
        isRangeStart,
        isRangeEnd,
        isInRange,
        isHoverEnd,
        isInHoverRange,
        isInHoverRangeInvalid
      })
    }

    return days
  })

  const loadAvailabilityForCurrentMonth = async () => {
    const isActive = typeof active === 'function' ? active() : active
    if (!isActive) return

    const currentRequestId = ++requestId.value
    const apt = typeof apartment === 'function' ? apartment() : apartment?.value ?? apartment
    const { calendarStart, calendarEnd } = getVisibleCalendarRange(currentMonth.value)

    await apiStore.loadAvailability(apt || null, {
      startDate: calendarStart,
      endDate: calendarEnd
    })

    return currentRequestId === requestId.value
  }

  const reportCalendarSearch = (action) => {
    const apt = typeof apartment === 'function' ? apartment() : apartment?.value ?? apartment
    trackCalendarSearch({
      month: currentMonth.value.getMonth() + 1,
      year: currentMonth.value.getFullYear(),
      apartment: apt,
      action,
    })
  }

  const goToPreviousMonth = async () => {
    hoverDate.value = null
    currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() - 1, 1)
    await loadAvailabilityForCurrentMonth()
    reportCalendarSearch('month_change')
  }

  const goToNextMonth = async () => {
    hoverDate.value = null
    currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 1)
    await loadAvailabilityForCurrentMonth()
    reportCalendarSearch('month_change')
  }

  const jumpToDate = (value) => {
    const parsed = parseDateInput(value)
    if (!parsed) return
    currentMonth.value = new Date(parsed.getFullYear(), parsed.getMonth(), 1)
  }

  const handleDayClick = (day) => {
    if (!selectable || !day.isClickable || !onRangeChange) return

    const result = resolveRangeClick({
      clickedDate: day.date,
      checkIn: getCheckIn(),
      checkOut: getCheckOut(),
      isRangeAvailable
    })

    selectionMessage.value = result.message
    hoverDate.value = null
    onRangeChange({ checkIn: result.checkIn, checkOut: result.checkOut })

    if (result.checkIn && result.checkOut) {
      reportCalendarSearch('date_range')
    }
  }

  const handleDayHover = (day) => {
    if (!selectable || selectionPhase.value !== 'pick-check-out') {
      hoverDate.value = null
      return
    }

    const selectedStart = parseDateInput(getCheckIn())
    if (!selectedStart) {
      hoverDate.value = null
      return
    }

    const normalizedDate = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate())
    hoverDate.value = normalizedDate > selectedStart ? day.date : null
  }

  const handleDayLeave = () => {
    hoverDate.value = null
  }

  const clearSelection = () => {
    selectionMessage.value = ''
    hoverDate.value = null
    onRangeChange?.({ checkIn: '', checkOut: '' })
  }

  watch(
    () => {
      const apt = typeof apartment === 'function' ? apartment() : apartment?.value ?? apartment
      return apt?._id || apt?.id || apt?.slug || apt?.name
    },
    () => {
      loadAvailabilityForCurrentMonth()
    }
  )

  watch(
    () => (typeof active === 'function' ? active() : active),
    (isActive) => {
      if (isActive) {
        loadAvailabilityForCurrentMonth()
      }
    },
    { immediate: true }
  )

  watch(
    () => getCheckIn(),
    (value) => {
      if (!value || !selectable) return
      jumpToDate(value)
    }
  )

  watch(
    () => [getCheckIn(), getCheckOut()],
    () => {
      if (getCheckOut()) {
        selectionMessage.value = ''
      }
    }
  )

  return {
    loading,
    error,
    monthLabel,
    calendarDays,
    selectionPhase,
    selectionMessage,
    goToPreviousMonth,
    goToNextMonth,
    handleDayClick,
    handleDayHover,
    handleDayLeave,
    clearSelection,
    loadAvailabilityForCurrentMonth
  }
}
